import { FindPatternGameData, FindPatternGame, createGame } from "./findpatterngame";
import { Profile, applyCompletedGameToProfile } from '../lib/profile';
import createStore, { Store } from 'unistore';
import { PageNavigator, PageNavigationType, PageNavigationCallback} from '../lib/navigator';
import { h } from 'preact';
import * as storage from '../lib/storage';
import { createTutorialData } from './findpatterngametutorial';
import { HelpPage } from './helppage';
import { StartPage } from './startpage';
import { PageAnimationDirection } from './pageanimator';
import { StateGetter } from '../lib/util';
import { RankProgress } from '../lib/rankprogress';
import { LevelCompletePage } from './levelcomplete';
import { SelectGamePage } from './selectgamepage';
import { TutorialCompletePage } from './tutorialcomplete';


interface VowelsState {
    currentGame: FindPatternGameData,
    profile: Profile,
}

type VowelsStore = Store<VowelsState>

export interface AppPage {
    pageName: string,
    pageBuilder: () => JSX.Element,
}
 
export class VowelsApp {
    store: VowelsStore
    navigator: PageNavigator<AppPage>
    onNavigateCallback: (newElement: JSX.Element, direction: PageAnimationDirection) => Promise<void>

    constructor() {
        this.store = createStore<VowelsState>({
            currentGame: null,
            profile: null,
        })
        this.navigator = new PageNavigator(this.onNavigate);
    }

/*    bindComponentToApp<TBoundProps,TStaticProps>(originalComponent: ComponentFactory<TStaticProps & TBoundProps>, binder: () => TBoundProps): ComponentFactory<TStaticProps> {
        let unsubscribe:Unsubscribe =  null;
        return bindComponent<TBoundProps, TStaticProps>(originalComponent, binder, (updater: ()=>void) => { unsubscribe = this.store.subscribe(updater) }, () => unsubscribe());
    }
*/
    onNavigate: PageNavigationCallback<AppPage> = (currentNav, type) => {
        trackPageView(currentNav.page.pageName);

        if (this.onNavigateCallback) {
            let newTopEl = currentNav.page.pageBuilder();
            return this.onNavigateCallback(newTopEl, type == PageNavigationType.Pop ? PageAnimationDirection.Right : PageAnimationDirection.Left)
        } else {
            return Promise.resolve();
        }
    }
    
    launch() {
        Promise.all([
            this.loadProfile(),
            this.loadGame()
        ])
        .then(() => { 
                if (this.store.getState().currentGame) {
                   this.navigateToMenu()
                } else {
                   this.navigateToTutorial(true)
                }
        })
    }

    loadProfile(): Promise<Profile> {
        return storage.loadProfile().then(
            profile => {
                this.store.setState({
                    profile: profile
                });
                return profile;
            }
        )
    }

    loadGame(): Promise<FindPatternGameData> {
        return storage.loadGame().then(
            (loadedGame) => {
                 this.store.setState({
                    currentGame: loadedGame,
                });
                return loadedGame
            },
            () => {
                this.store.setState({
                    currentGame: null
                });
                return null
            }
        );
    }

    saveGame = (gameState: FindPatternGameData) => {
        storage.saveGame(gameState);
        this.store.setState({currentGame: gameState})
    }

    saveProfile = (profile: Profile) => {
        storage.saveProfile(profile)
        this.store.setState({profile: profile})
    }

    launchGame(gameData: FindPatternGameData) {
        this.store.setState({ currentGame: gameData });
        this.navigateToGame(gameData);
    }

    launchQuickGame() {
        this.launchGame(createGame(3));
    }

    launchNormalGame() {
        this.launchGame(createGame(4));
    }

    launchLargeGame() {
        this.launchGame(createGame(5));
    }

    resumeCurrentGame() {
        this.navigateToGame(this.store.getState().currentGame);
    }

    playSameGameTypeAgain() {
        this.navigateToGame( createGame(this.store.getState().currentGame.board.Cols), true )
    }

    redoTutorial() {
        this.navigateToTutorial(false);
    }

    skipTutorial() {
        this.navigator.navigateReplace(this.menuNav)
    }

    navigateBack() {
        this.navigator.navigatePop();
    }

    navigateToMenu() {
        this.navigator.navigatePush(this.menuNav)
    }

    showCompleteScreen = (startingRankProgress: RankProgress, foundWords: number) => {
        this.navigator.navigateReplace({
            pageName: "LevelComplete",
            pageBuilder: () => <LevelCompletePage 
                foundWords={foundWords}
                startingProgress={startingRankProgress}
                onBack={()=>this.navigateBack()}
                onPlayAgain={()=> this.navigateToGame( createGame(this.store.getState().currentGame.board.Cols), true )}
            />
        })
    }

    showTutorialCompleteScreen = (isInitialRun: boolean) => {
        this.navigator.navigateReplace({
            pageName: "TutorialComplete",
            pageBuilder: () => <TutorialCompletePage 
                isInitialRun={isInitialRun}
                onBack={() => this.navigateBack()}
                onContinue={() =>  this.navigator.navigateReplace(this.menuNav) }
                onPlayGame={() =>  this.continueFromTutorial() }
            />
        })
    }

    continueFromTutorial = () => {
        //we didn't come from the main menu, shim one in here so that if they press back from the started game, they end up on the menu
        this.navigator.replace(this.menuNav);
        this.navigateToGame(createGame(3))
    }

    navigateToHelpPage() {
        this.navigator.navigatePush({
            pageName: "HelpPage",
            pageBuilder: ()=> <HelpPage  vowelsApp={this} />
        })
    }


    levelCompleted(completeGameData: FindPatternGameData) {
        let profile = this.store.getState().profile;
       
        let updatedProfile = applyCompletedGameToProfile(profile, completeGameData);
        this.saveProfile(updatedProfile);
        this.store.setState({
            profile: updatedProfile
        })

        this.showCompleteScreen(profile.rankProgress, completeGameData.foundWords.length)
    }

    navigateToTutorial(isInitialRun: boolean) {
        this.navigator.navigatePush({
            pageName: "Tutorial",
            pageBuilder: () => 
                <FindPatternGame
                    initialGameData={createTutorialData()}
                    isTutorial={true}
                    canSkipTutorial={isInitialRun}
                    onBackPressed={() => this.navigateBack()}
                    onSkipTutorial={() => this.skipTutorial()}
                    onComplete={() => this.showTutorialCompleteScreen(isInitialRun)}
                />
        })
    }

    navigateToGame(gameData: FindPatternGameData, replace: boolean = false) {
        let state:FindPatternGameData = null;
        let stateGetter: StateGetter<FindPatternGameData> = null;

        let appPage = {
            pageName: "game",
            pageBuilder: () => 
                <FindPatternGame
                    initialGameData={gameData}
                    isTutorial={false}
                    onHibernate={(getter) => stateGetter = getter}
                    hibernateState={state}
                    saveGame={this.saveGame}
                    onShowHelpPressed={() => this.navigateToHelpPage()}
                    onBackPressed={()=>this.navigateBack()}
                    onComplete={(data)=>this.levelCompleted(data)}
                />
        }
        let onsuspend = () => {if (stateGetter) state = stateGetter() }

        if (replace) {
            this.navigator.navigateReplace(appPage, onsuspend)
        } else {
            this.navigator.navigatePush(appPage, onsuspend)
        }            
    }

    navigateToSelectGamePage() {
        this.navigator.navigatePush({
            pageName: "SelectGame",
            pageBuilder: () => 
                <SelectGamePage
                    onBack={() => this.navigateBack()}
                    onStartGame={({cols})=> this.navigateToGame( createGame(cols), true)  }
                />
        })
    }

    menuNav: AppPage = {
        pageName: "MainMenu",
        pageBuilder: () => 
            <StartPage 
                launchGame={ () => this.navigateToSelectGamePage()}
                resumeCurrentGame= {() => this.resumeCurrentGame()}
                redoTutorial={() => this.redoTutorial()}
                canResume={!!this.store.getState().currentGame}
                profile={this.store.getState().profile}
            />
    }           
}
